import { type RenderableTreeNode, Tag } from "@markdoc/markdoc";

import type { SupportedLanguage } from "@mountayaapp/locales";

import {
  type DocEntry,
  getNavManifest,
  listEntries,
  resolveTab,
} from "../../content/rules/registry";

/**
 * @mountayaapp/docs search index. This render-free module turns the parsed
 * content registry into a flat list of searchable records the Spotlight command
 * palette consumes. Each page contributes one record for the page itself (its
 * title and description) plus one record per level-2 section (the heading and
 * the plain text beneath it), so a query can land on either a page or a specific
 * section anchor. It reuses the registry's parsed trees and tables of contents
 * rather than re-parsing or re-slugifying, so an anchor is always a real heading
 * id the page renders.
 */

/**
 * SearchRecord is one searchable unit. A page record carries an empty `heading`
 * and `anchor` and the page description as its `text`; a section record carries
 * the level-2 heading, its toc `anchor`, and the section body as `text`. `group`
 * is the localization key of the record's owning navigation group (or its header
 * tab for the pages reached only through a tab), resolved to a label by the
 * consumer so this module stays render-free.
 */
export interface SearchRecord {
  anchor: string;
  group: string;
  heading: string;
  href: string;
  pageTitle: string;
  text: string;
}

/**
 * SECTION_TEXT_LIMIT caps a section's indexed body text so a long section keeps
 * the record small and the result description readable.
 */
const SECTION_TEXT_LIMIT = 300;

/**
 * collectText concatenates the textual content of a renderable node, reading
 * string leaves and recursing into tags and arrays. It backs both section bodies
 * and the title of a heading node.
 */
const collectText = (node: RenderableTreeNode): string => {
  if (typeof node === "string") {
    return node;
  }

  if (Tag.isTag(node)) {
    return node.children.map(collectText).join(" ");
  }

  if (Array.isArray(node)) {
    return node.map(collectText).join(" ");
  }

  return "";
};

/**
 * normalizeWhitespace collapses every run of whitespace to a single space and
 * trims the ends, so text stitched from many nodes reads as one clean line.
 */
const normalizeWhitespace = (raw: string): string => raw.replace(/\s+/g, " ").trim();

/**
 * truncate shortens text to at most `max` characters, cutting on a word boundary
 * and appending an ellipsis, so a long section body does not overflow a result.
 */
const truncate = (text: string, max: number): string => {
  if (text.length <= max) {
    return text;
  }

  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const trimmed = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;

  return `${trimmed}…`;
};

/**
 * buildSectionText walks a page's renderable tree and returns the normalized,
 * truncated body text of each level-2 section, keyed by the section's heading id.
 * Text accumulates into the current level-2 section until the next level-2
 * heading; a deeper heading stays within its section (its title is searchable
 * there), and text before the first level-2 heading belongs to no section (the
 * page record already carries the lead via the frontmatter description). The
 * heading ids are the same ones the registry's toc exposes, so the caller can
 * join a section body to its toc entry by id.
 */
const buildSectionText = (tree: RenderableTreeNode): Map<string, string> => {
  const fragments = new Map<string, string[]>();
  let currentId: string | null = null;

  const visit = (node: RenderableTreeNode): void => {
    if (typeof node === "string") {
      if (currentId !== null && node.trim() !== "") {
        fragments.get(currentId)?.push(node);
      }

      return;
    }

    if (Array.isArray(node)) {
      for (const child of node) {
        visit(child);
      }

      return;
    }

    if (!Tag.isTag(node)) {
      return;
    }

    if (node.name === "Heading") {
      const level = typeof node.attributes.level === "number" ? node.attributes.level : 0;
      const id = typeof node.attributes.id === "string" ? node.attributes.id : "";

      if (level === 2 && id !== "") {
        currentId = id;
        if (!fragments.has(id)) {
          fragments.set(id, []);
        }
      } else if (level <= 2) {
        // A page title (level 1) or an unanchored level-2 heading closes the
        // current section without opening a new one.
        currentId = null;
      } else if (currentId !== null) {
        fragments.get(currentId)?.push(collectText(node));
      }

      return;
    }

    for (const child of node.children) {
      visit(child);
    }
  };

  visit(tree);

  const sections = new Map<string, string>();
  for (const [id, parts] of fragments) {
    sections.set(id, truncate(normalizeWhitespace(parts.join(" ")), SECTION_TEXT_LIMIT));
  }

  return sections;
};

/**
 * buildSearchRecords produces the search records for one language. Curated pages
 * come first, in the sidebar's reading order and tagged with their navigation
 * group, so grouped results follow the same order the sidebar does; the pages
 * reached only through a header tab (the interactive API surfaces and the
 * changelog) are appended and tagged with their owning tab. Each page yields a
 * page record plus one record per level-2 heading, anchored by the toc id.
 */
export const buildSearchRecords = (lang: SupportedLanguage): SearchRecord[] => {
  const entriesForLang = listEntries().filter((entry) => entry.lang === lang);
  const entryByHref = new Map<string, DocEntry>();
  for (const entry of entriesForLang) {
    entryByHref.set(entry.href, entry);
  }

  const records: SearchRecord[] = [];
  const indexed = new Set<string>();

  const pushEntry = (entry: DocEntry, group: string): void => {
    indexed.add(entry.href);

    records.push({
      anchor: "",
      group,
      heading: "",
      href: entry.href,
      pageTitle: entry.frontmatter.title,
      text: truncate(normalizeWhitespace(entry.frontmatter.description ?? ""), SECTION_TEXT_LIMIT),
    });

    const sectionText = buildSectionText(entry.tree);
    for (const toc of entry.toc) {
      if (toc.level !== 2) {
        continue;
      }

      records.push({
        anchor: toc.id,
        group,
        heading: toc.title,
        href: entry.href,
        pageTitle: entry.frontmatter.title,
        text: sectionText.get(toc.id) ?? "",
      });
    }
  };

  for (const group of getNavManifest(lang)) {
    for (const item of group.items) {
      const entry = entryByHref.get(item.href);
      if (entry && !indexed.has(entry.href)) {
        pushEntry(entry, group.labelKey);
      }
    }
  }

  for (const entry of entriesForLang) {
    if (!indexed.has(entry.href)) {
      pushEntry(entry, resolveTab(entry.href).labelKey);
    }
  }

  return records;
};
